import { Search } from "@outschool/business-rules";
import {
  RequestClassInquiryMutation,
  RequestClassInquiryMutationVariables
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import omit from "lodash/omit";
import React, { useContext, useState } from "react";

import { SearchFiltersContext } from "../../../lib/useSearchFilters";
import {
  classInquiriesByUserQuery,
  requestClassInquiryMutation
} from "../../../queries/ClassInquiryQueries";
import { useTeacherPullExperiment } from "../../search/useTeacherPullExperiment";
import { useChatContext } from "../chatProvider";
import { ChatInputField } from "../components/ChatInputField";
import { LearnerSelection } from "../components/LearnerSelection";

export const SendClassRequestInput = () => {
  const [inputValue, setInputValue] = useState("");

  const { t } = useTranslation("client\\components\\chat\\teacherPullChat");

  const trackEvent = useTrackEvent();
  const { currentUser } = useSession();
  const searchContext = useContext(SearchFiltersContext);
  const { sendClassInquirySubmit, classInquiryCreated, selectedLearner } =
    useChatContext();

  const { isTreatment: isV2_5Treatment, triggerExperiment: triggerV2_5 } =
    useTeacherPullExperiment();

  const [requestClassInquiry] = useMutation<
    RequestClassInquiryMutation,
    RequestClassInquiryMutationVariables
  >(requestClassInquiryMutation, {
    refetchQueries: [
      {
        query: classInquiriesByUserQuery,
        variables: { userUid: currentUser?.uid }
      }
    ]
  });

  const { filters } = searchContext;

  const cleanedFilters = {
    ...omit(filters, [Search.SCHEDULE_PLANNER_TRACKER])
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    trackEvent("teacher_pull_chat_request_submit_button_clicked");

    if (inputValue) {
      sendClassInquirySubmit(inputValue);

      try {
        triggerV2_5();

        const { data: classInquiryData } = await requestClassInquiry({
          variables: {
            userUid: currentUser?.uid || "",
            inquiryText: inputValue,
            learnerUid: selectedLearner?.uid,
            searchFilters: cleanedFilters,
            isV2_5Treatment: isV2_5Treatment
          }
        });

        if (!!classInquiryData?.requestClassInquiry) {
          classInquiryCreated(classInquiryData?.requestClassInquiry);

          if (isV2_5Treatment) {
            trackEvent("ai_guide_first_request_response_received", {
              followUpQuestion:
                classInquiryData?.requestClassInquiry.followUpQuestion
            });
          }
        }
      } catch (error) {
        OsPlatform.captureError(error);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ChatInputField
        inputHelperText={t(
          "Example: “I’m looking for a writing tutor for weekday evenings...”"
        )}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        handleSubmit={handleSubmit}
      >
        <LearnerSelection label={t("Select a Learner")} />
      </ChatInputField>
    </form>
  );
};
